import { useMemo, useCallback } from 'react';
import type {
  DomainEntity,
  AnnotatedJsonSchema,
  TreeNode,
  Tree,
} from '@web-config-app/core';
import { useTranslate } from '@web-config-app/core-react';
import { createEntityTree } from '../../utils/create-entity-tree/create-entity-tree';

export interface UseEntityTreeResult {
  entityTree: Tree;
  nodeMap: Map<string, TreeNode>;
  handleNodeToggle: (nodeId: string) => void;
}

export interface UseEntityTreeProps {
  schema: AnnotatedJsonSchema;
  data: any;
  onSelect?: (...args: any) => void;
  onSchemaPropertyRemoved?: (dataPath: string, propertyName: string) => void;
  domainEntity: DomainEntity;
}

const flattenTree = (tree: Tree) => {
  const nodes: TreeNode[] = [];
  const flattenTreeHelper = (t: Tree) => {
    t.forEach(({ children, ...node }: TreeNode) => {
      nodes.push(node);
      if (children?.length && children.length > 0) {
        flattenTreeHelper(children);
      }
    });
  };

  flattenTreeHelper(tree);
  return nodes;
};

export const useEntityTree = ({
  schema,
  data,
  onSelect,
  onSchemaPropertyRemoved,
  domainEntity,
}: UseEntityTreeProps) => {
  const translate = useTranslate();

  const isEntityUsingConfigAppBackend = domainEntity.isUsingConfigAppBackend;

  const entityTree = useMemo(
    () =>
      createEntityTree(
        { schema, data, onSchemaPropertyRemoved },
        translate,
        isEntityUsingConfigAppBackend,
      ),
    [
      translate,
      data,
      schema,
      onSchemaPropertyRemoved,
      isEntityUsingConfigAppBackend,
    ],
  );

  const nodeMap = useMemo(() => {
    const flatTree = flattenTree(entityTree);
    return new Map(flatTree.map((node: TreeNode) => [node.id, node]));
  }, [entityTree]);

  const handleNodeToggle = useCallback(
    (nodeId: string) => {
      const item = nodeMap.get(nodeId);
      onSelect?.(item);
    },
    [nodeMap, onSelect],
  );

  return {
    entityTree,
    nodeMap,
    handleNodeToggle,
  };
};
